import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import {
  CommonFormElementSize,
  FloatingStrategy,
  PopupPlacement,
} from '@/common/types'
import { getValidityStateObject } from '@/common/utils'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { t } from '@/localization/localization'

import DapDSOptionList from '../option-list/option-list.component'
import styles from './select.scss?inline'

/**
 * `dap-ds-select`
 * @summary A select is a form element that allows the user to select one option from a set.
 * @element dap-ds-select
 * @title - Select
 *
 * @event {{ value: string }} dds-change - Fired when the select value changes.
 * @event {{ void }} dds-blur - Emitted when the select loses focus.
 * @event {{ void }} dds-focus - Emitted when the select gains focus.
 * @event {{ void }} dds-opened - Emitted when the select dropdown is opened.
 * @event {{ void }} dds-closed - Emitted when the select dropdown is closed.
 *
 * @property {string} value - The value of the select.
 * @property {string} label - The label of the select.
 * @property {string} description - The description of the select.
 * @property {string} tooltip - The tooltip of the select.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the select.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the select. Default is `sm`.
 * @property {boolean} disabled - Whether the select is disabled.
 * @property {boolean} required - Whether the select is required.
 * @property {boolean} readonly - Whether the select is readonly.
 * @property {boolean} autofocus - Whether the select is autofocus.
 * @property {string} feedback - The feedback of the select.
 * @property {negative | positive | warning} feedbackType - The feedback type of the select. Can be `negative`, `positive`, or `warning`.
 * @property {string} status - The status of the select. Can be `success` or `error`.
 * @property {boolean} optional - The optional state of the select.
 * @property {string} optionalLabel - The optional label of the select.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 *
 * @csspart base - The main select container.
 * @csspart trigger - The trigger button of the select.
 * @csspart label - The label of the select.
 * @csspart description - The description of the select.
 * @csspart feedback - The feedback of the select.
 * @csspart tooltip - The tooltip of the select.
 * @csspart option-list - The option list of the select.
 *
 * @cssproperty --dds-select-min-height - Minimum height of the select component (default: var(--dds-spacing-1000))
 * @cssproperty --dds-select-padding - Padding of the select component (default: var(--dds-spacing-300))
 * @cssproperty --dds-select-column-gap - Column gap of the select component (default: var(--dds-spacing-100))
 * @cssproperty --dds-select-border-color - Border color of the select component (default: var(--dds-border-neutral-base))
 * @cssproperty --dds-select-border-radius - Border radius of the select component (default: var(--dds-radius-base))
 * @cssproperty --dds-select-bg-color - Background color of the select component (default: var(--dds-background-neutral-subtle))
 * @cssproperty --dds-select-text-color - Text color of the select component (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-select-icon-right - Right position of the icon (default: var(--dds-spacing-200))
 * @cssproperty --dds-select-icon-color - Color of the icon (default: var(--dds-icon-neutral-subtle))
 * @cssproperty --dds-select-text-margin-right - Right margin of the text (default: var(--dds-spacing-600))
 * @cssproperty --dds-select-disabled-border-color - Border color when disabled (default: var(--dds-background-neutral-stronger))
 * @cssproperty --dds-select-disabled-bg-color - Background color when disabled (default: var(--dds-background-neutral-stronger))
 * @cssproperty --dds-select-disabled-text-color - Text color when disabled (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-select-error-border-color - Border color in error state (default: var(--dds-border-negative-base))
 * @cssproperty --dds-select-success-border-color - Border color in success state (default: var(--dds-border-positive-base))
 * @cssproperty --dds-select-xs-min-height - Minimum height for extra small size (default: var(--dds-spacing-800))
 * @cssproperty --dds-select-xs-padding - Padding for extra small size (default: var(--dds-spacing-200))
 * @cssproperty --dds-select-xs-font-size - Font size for extra small size (default: var(--dds-font-sm))
 * @cssproperty --dds-select-lg-min-height - Minimum height for large size (default: var(--dds-spacing-1200))
 * @cssproperty --dds-select-lg-padding - Padding for large size (default: var(--dds-spacing-400))
 * @cssproperty --dds-select-lg-font-size - Font size for large size (default: var(--dds-font-lg))
 * @cssproperty --dds-select-lg-icon-right - Right position of the icon for large size (default: var(--dds-spacing-300))
 * @cssproperty --dds-select-lg-icon-color - Color of the icon for large size (default: var(--dds-text-icon-neutral-subtle))
 * @cssproperty --dds-select-popup-bg-color - Background color of the popup (default: var(--dds-background-neutral-subtle))
 *
 * @slot - The option list of the select.
 * @slot indicator-icon - The indicator icon of the select.
 */
@customElement('dap-ds-select')
export default class DapDSSelect extends GenericFormElement {
  /**
   * @ignore
   */
  @query('button') triggerButton!: HTMLButtonElement

  /**
   * @ignore
   */
  @query('dap-ds-option-list') optionList!: DapDSOptionList

  /** The popup placement of the select
   * @type {'top' | 'top-start' | 'top-end' | 'right' | 'right-start'| 'right-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end'}
   */
  @property({ reflect: true }) placement: PopupPlacement = 'bottom-start'

  /** Whether the select dropdown is opened.*/
  @property({ type: Boolean, reflect: true }) opened? = false

  /** The placeholder of the select. */
  @property() placeholder: string | null

  /** The sync size of the select dropdown */
  @property({ type: Boolean }) sync?: boolean

  /** Whether the select is in mobile mode. */
  @property({ type: Boolean }) isMobile?: boolean = false

  /** The loading state of the select. */
  @property({ type: Boolean }) loading?: boolean = false

  /** The max heigth of the select dropdown. */
  @property({ type: Number }) maxHeight?: number

  /** Whether the select dropdown indicator is animated or not */
  @property({ type: Boolean }) noAnimation?: boolean = false

  /** The floating strategy of the select dropdown
   * @type {'absolute' | 'fixed'}
   */
  @property({ reflect: true }) floatingStrategy: FloatingStrategy = 'absolute'

  /**
   * @ignore
   */
  @state() selectedItem: any

  static readonly styles = unsafeCSS(styles)

  private readonly handleClick = () => {
    if (this.readonly) {
      return
    }

    this.opened ? this.hideDropDown() : this.showDropDown()
  }

  private readonly handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === 'Tab' || event.key === 'Shift') {
      return
    }

    if (this.readonly) {
      return
    }

    event.preventDefault()

    if (event.key === 'Enter' || event.key === ' ') {
      return this.opened ? this.hideDropDown() : this.showDropDown()
    }

    if (event.key === 'Escape') {
      this.hideDropDown()
      return this.focus()
    }

    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      if (!this.opened) {
        return this.showDropDown()
      }

      return
    }

    this.emit('dds-keydown', event)

    // typing
    this.showDropDown(event.key)
  }

  private readonly handleListKeyDown = (event: CustomEvent) => {
    if (event.detail.key === 'Escape') {
      this.hideDropDown()
    }
  }

  private readonly handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) {
      this.hideDropDown()
    }
  }

  private readonly handleFocus = () => {
    this.emit('dds-focus')
  }

  private readonly handleBlur = () => {
    this.emit('dds-blur')
  }

  formResetCallback() {
    this.internals.setValidity({})
    this.internals.setFormValue(this.defaultValue)
    this.value = this.defaultValue
    this.selectedItem = this.defaultValue
  }

  async showDropDown(key?: string) {
    if (this.opened) {
      return
    }

    this.opened = true

    document.addEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    this.optionList.captureFocus(key)

    this.emit('dds-opened')
  }

  async hideDropDown() {
    if (!this.opened) {
      return
    }

    this.opened = false

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    this.optionList.removeFocus()

    this.focus()

    this.emit('dds-closed')
  }

  get focusElement() {
    return this.triggerButton
  }

  // custom validation
  get validity(): ValidityState {
    if (this.internals.validity) {
      return this.internals.validity
    } else {
      return getValidityStateObject({
        valueMissing: this.required && !this.selectedItem,
      })
    }
  }

  get validationMessage(): string {
    return t('form.validationMessage.select')
  }

  checkValidity() {
    return this.required ? !!this.selectedItem : true
  }

  reportValidity() {
    return this.required ? this.checkValidity() : true
  }

  setValidity() {
    if (!this.checkValidity()) {
      this.internals.setValidity(
        {
          valueMissing: this.required && !this.selectedItem,
        },
        this.validationMessage,
        this.triggerButton,
      )
    } else {
      this.internals.setValidity({})
    }
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  private readonly handleSelectedChange = (event: CustomEvent) => {
    const val = Object.keys(event.detail).length ? event.detail : null
    this.selectedItem = val
    this.hideDropDown()
    this.value = this.selectedItem?.value
    this.emit('dds-change', this.selectedItem)

    this.setValidity()
  }

  renderTriggerElement() {
    return html` <button
      slot="trigger"
      part="trigger"
      role="combobox"
      class="${clsx('select', `select--${this.size}`, {
        'select--disabled': this.disabled,
        'select--readonly': this.readonly,
        'select--opened': this.opened,
        'select--loading': this.loading,
        'select--success': this.status === 'success',
        'select--error': this.status === 'error',
        'select--no-animation': this.noAnimation,
        'select--optional': this.optional,
      })}"
      .aria-expanded=${this.opened}
      aria-haspopup="listbox"
      aria-labelledby=${this.label ? 'label' : ''}
      aria-label=${this.ariaLabel ?? ''}
      aria-describedby="${this.description ? 'description' : ''}${this.feedback
        ? ' feedback'
        : ''}"
      aria-disabled=${this.disabled}
      .aria-controls=${'option-list'}
      aria-activedescendant=${this.selectedItem?.id}
      ?required=${this.required}
      ?readonly=${this.readonly}
      ?autofocus=${this.autofocus}
      ?disabled=${this.disabled}
      @blur=${this.handleBlur}
      @focus=${this.handleFocus}
      @keydown=${this.handleKeyDown}
      @click=${this.handleClick}>
      <span class="text"
        >${this.selectedItem ? this.selectedItem.text : this.placeholder}</span
      >
      <div class="icon">
        ${this.loading
          ? html`<dap-ds-icon-loading-spinner></dap-ds-icon-loading-spinner>`
          : html`<slot name="indicator-icon"
              ><dap-ds-icon-arrow-right-s-line></dap-ds-icon-arrow-right-s-line
            ></slot>`}
      </div>
    </button>`
  }

  renderOptionList() {
    return html` <dap-ds-option-list
      class="option-list"
      part="option-list"
      id="option-list"
      searchMode="typehead"
      size=${ifDefined(this.size)}
      value=${ifDefined(this.value)}
      disableDelaySet
      @dds-option-change=${this.handleSelectedChange}
      @dds-keydown=${this.handleListKeyDown}>
      <slot></slot>
    </dap-ds-option-list>`
  }

  render() {
    return html` <div class="label-container">
        ${this.label &&
        html`<dap-ds-form-label
          part="label"
          id="label"
          .size=${this.size}
          .required=${this.required}
          .optional=${this.optional}
          .optionalLabel=${this.optionalLabel}
          .subtle=${this.subtle}
          .disabled=${this.disabled}
          class=${clsx('label', {
            'label--has-description': !this.description,
          })}
          for="input"
          label=${ifDefined(this.label)}>
        </dap-ds-form-label>`}
        ${this.tooltip &&
        html` <dap-ds-tooltip
          part="tooltip"
          content=${this.tooltip}
          mode="toggle"
          placement=${this.tooltipPlacement}>
          <dap-ds-icon-button
            clean
            slot="trigger"
            size="lg"
            aria-label=${this.tooltipAriaLabel}
            class="input-group__toggle-button">
            <dap-ds-icon name="information-line"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-tooltip>`}
      </div>
      ${this.description &&
      html`
        <dap-ds-typography
          part="description"
          variant="description"
          size=${ifDefined(this.size as CommonFormElementSize)}
          id="description">
          ${this.description}
        </dap-ds-typography>
      `}
      <div>
        ${!this.isMobile
          ? html`<dap-ds-popup
              floatingStrategy=${ifDefined(this.floatingStrategy)}
              part="base"
              class="select-popup"
              ?opened=${this.opened}
              ?hasArrow=${false}
              overflow
              placement=${this.placement}
              maxHeight=${ifDefined(this.maxHeight)}
              ?sync=${this.sync}
              size=${ifDefined(this.size) as 'lg' | 'sm' | 'xs'}>
              ${this.renderTriggerElement()} ${this.renderOptionList()}
            </dap-ds-popup>`
          : html`
              ${this.renderTriggerElement()}
              <dap-ds-tray ?open=${this.opened}
                >${this.renderOptionList()}</dap-ds-tray
              >
            `}
        ${this.feedback &&
        html`<dap-ds-feedback
          part="feedback"
          feedbackType=${ifDefined(this.feedbackType)}
          feedbackSize="${ifDefined(this.size as 'lg' | 'sm' | 'xs')}"
          id="feedback"
          .subtle=${this.subtle}>
          ${this.feedback}
        </dap-ds-feedback>`}
      </div>`
  }
}
